import type {
  Control,
  FieldArrayPath,
  FieldArrayPathValue,
  FieldValues,
  Path,
} from "react-hook-form";
import { Controller, useFieldArray } from "react-hook-form";
import { useMemo } from "react";

import { RelationObjectType } from "@enums/relations.enum";
import { useCurrencies } from "@hooks/useCurrencies";
import { IAttribute, IAttributeFieldValue, IAttributeValue } from "@interfaces/attribute.interface";
import { ICurrency } from "@interfaces/currency.interface";
import { AttributeRow } from "./AttributeRow";
import { DEFAULT_CURRENCY_SYMBOL } from "./constants";
import { generateDefaultAttributeFieldValues } from "./utils";
import { cn } from "@utils/tailwind.util";

/** Extracts only FieldArrayPath keys whose value is IAttributeFieldValue[] */
type AttributeFieldArrayPath<T extends FieldValues> = {
  [K in FieldArrayPath<T>]: FieldArrayPathValue<T, K> extends IAttributeFieldValue[] ? K : never;
}[FieldArrayPath<T>];

interface AttributesFormProps<T extends FieldValues> {
  // Field array path, e.g. "attribute_values"
  name: AttributeFieldArrayPath<T>;
  control: Control<T>;
  attributes: IAttribute[];
  // Entity these attributes belong to; resolves the per-entity-type `is_required` flag.
  contentType: RelationObjectType;
  // Ids of attributes shared between the company and its active deal; shows the link icon.
  commonAttributeIds?: Set<string>;
  hideCommonAttributeIcons?: boolean;
  onCommonAttributeClick?: (attribute: IAttribute) => void;
  onAttributeSave?: (value: IAttributeFieldValue, isCommon: boolean) => void;
  // The entity's stored values. Only needed alongside `onAttributeSave`, which reverts a cleared
  // required field to the stored entry rather than to whatever was last typed into the form.
  savedValues?: IAttributeValue[];
  // Currency shown on CURRENCY attributes; falls back to the organization default.
  currency?: ICurrency | null;
  // Never offered by RELATION search — typically the entity being edited.
  excludedRelationIds?: string[];
  className?: string;
}

export function AttributesForm<T extends FieldValues>({
  name,
  control,
  attributes,
  contentType,
  commonAttributeIds,
  hideCommonAttributeIcons = false,
  onCommonAttributeClick,
  onAttributeSave,
  savedValues,
  currency,
  excludedRelationIds,
  className = "flex flex-col gap-4",
}: AttributesFormProps<T>) {
  // Built with the same helper that seeds the form, so a revert restores the exact seeded shape.
  const savedByAttribute = useMemo(
    () =>
      new Map(
        generateDefaultAttributeFieldValues(attributes, savedValues).map((entry) => [
          entry.attribute,
          entry,
        ])
      ),
    [attributes, savedValues]
  );

  const { data: currencies } = useCurrencies();
  const currencySymbol =
    currency?.symbol ??
    currencies?.find((item) => item.order === 0)?.currency.symbol ??
    DEFAULT_CURRENCY_SYMBOL;

  const { fields: rawFields } = useFieldArray({ control, name: name as FieldArrayPath<T> });
  const fields = rawFields as unknown as (IAttributeFieldValue & { id: string })[];

  const rows = attributes
    .map((attribute) => ({
      attribute,
      index: fields.findIndex((f) => f.attribute === attribute.id),
    }))
    .filter((row) => row.index !== -1);

  return (
    <div className={cn(" mb-4", className)}>
      {rows.map(({ attribute, index }) => {
        const isCommon = !!commonAttributeIds?.has(attribute.id);

        return (
          <Controller
            key={attribute.id}
            control={control}
            name={`${name}.${index}` as Path<T>}
            render={({
              field: { value: fieldValue, onChange },
              fieldState: { invalid, error },
            }) => (
              <AttributeRow
                attribute={attribute}
                contentType={contentType}
                rowId={`${name}.${index}`}
                value={fieldValue as IAttributeFieldValue}
                savedValue={savedByAttribute.get(attribute.id)}
                onChange={onChange}
                isInvalid={invalid}
                errorMessage={error?.message}
                currencySymbol={currencySymbol}
                isCommon={isCommon}
                showCommonIcon={isCommon && !hideCommonAttributeIcons}
                excludedRelationIds={excludedRelationIds}
                onCommonAttributeClick={onCommonAttributeClick}
                onAttributeSave={onAttributeSave}
              />
            )}
          />
        );
      })}
    </div>
  );
}
